/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 29-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Pick a trade with no dedicated provider template yet, to start building one
 *                (seeded from the Global Default) via the "+" affordance on the Provider tab.
 */

import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Trade } from '../types';
import { tradePalette } from '../library/libraryModel';

export interface AddProviderDefaultModalProps {
  open: boolean;
  /** Trades with no dedicated provider template yet (i.e. !hasTradeTemplate(trade)). */
  trades: Trade[];
  onClose: () => void;
  onSelect: (tradeId: string) => void;
}

/** "+" picker on the Provider tab: choose any trade lacking its own provider default, to start
 *  building one (opens the Builder seeded from the Global Default, per TradeTemplateSystem's
 *  provider-scope seeding). Only lists trades that don't already have a dedicated template. */
export default function AddProviderDefaultModal({ open, trades, onClose, onSelect }: AddProviderDefaultModalProps) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return query ? trades.filter(t => t.name.toLowerCase().includes(query) || t.category.toLowerCase().includes(query)) : trades;
  }, [q, trades]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add a provider default template</DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Pick a trade that has no dedicated provider template yet. It'll open in the Builder seeded
          from the Global Default — edit it, then Publish… to the provider (and any tenants, if needed).
        </Typography>
        <TextField fullWidth size="small" autoFocus value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search trades…" sx={{ mb: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        {filtered.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.disabled', fontSize: 13 }}>
            {trades.length === 0 ? 'Every trade already has a dedicated provider template.' : 'No trade matches your search.'}
          </Box>
        ) : (
          <List sx={{ maxHeight: 360, overflow: 'auto' }}>
            {filtered.map(t => {
              const [softBg, ink] = tradePalette(t.id);
              return (
                <ListItemButton key={t.id} onClick={() => onSelect(t.id)} sx={{ borderRadius: 1.5, mb: 0.5 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1.25, bgcolor: softBg, color: ink, flexShrink: 0, mr: 1.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12 }}>{t.name[0]}</Box>
                  <ListItemText primary={t.name} secondary={t.category} />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
